/**
 * API service for communicating with the valuepath backend.
 *
 * Handles:
 * - Projects CRUD
 * - Folders CRUD
 * - Files (with project/folder context)
 * - Conversations (project-scoped)
 * - Type mapping between backend and frontend
 */

import { auth } from '../firebase';
import type { SyncSkip } from '../utils/syncSkipped';
import type { MemberAccess, Project, ProjectInvite, ProjectMember, ProjectRole, TabItemType, TreeItem, TreeItemType } from '../types';
import { getApiBaseUrl } from './apiUrl';
import { fileStem } from '../utils/filename';

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get the base URL for API requests.
 *
 * Routing strategy:
 * - Local dev: VITE_API_URL (http://localhost:8080) or empty for docker-compose proxy
 * - valuepath-prod: https://api.valuepath.io (direct Cloud Run via custom domain)
 * - Managed stacks: same origin, API at /api (no custom domain involved)
 *
 * We use custom domains instead of Firebase Hosting rewrites because:
 * - Firebase Hosting buffers responses, breaking SSE streaming
 * - Firebase Hosting has a 60-second timeout, killing long conversation sessions
 */
export { getApiBaseUrl };

export const API_BASE_URL = getApiBaseUrl();

// ─────────────────────────────────────────────────────────────────────────────
// Types (Backend Response Formats)
// ─────────────────────────────────────────────────────────────────────────────

interface ApiProject {
  id: string;
  name: string;
  description: string | null;
  settings: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
  /** Sharing: the acting user's role, the roster size, and who created it. */
  role?: 'owner' | 'member';
  member_count?: number;
  creator?: {
    id: string | null;
    display_name: string | null;
    email: string | null;
    photo_url: string | null;
  } | null;
}

interface ApiFolder {
  id: string;
  name: string;
  project_id: string;
  agent_id: string;
  parent_folder_id: string | null;
  sync_status: 'none' | 'active' | 'error';
  integration_id: string | null;
  provider_folder_id: string | null;
  last_synced_at: string | null;
  sync_error: string | null;
  sync_skipped?: SyncSkip[];
  created_at: string | null;
  updated_at: string | null;
}

interface ListFoldersResponse {
  folders: ApiFolder[];
  project_id: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Type Mappers
// ─────────────────────────────────────────────────────────────────────────────

function mapApiFolderToTreeItem(folder: ApiFolder): TreeItem {
  return {
    id: folder.id,
    name: folder.name,
    type: 'folder',
    parentId: folder.parent_folder_id,
    agentId: folder.agent_id,
    syncStatus: folder.sync_status !== 'none' ? folder.sync_status : undefined,
    syncIntegrationId: folder.integration_id ?? undefined,
    syncError: folder.sync_error ?? undefined,
    syncSkipped: folder.sync_skipped?.length ? folder.sync_skipped : undefined,
    lastSyncedAt: folder.last_synced_at ?? undefined,
    createdAt: folder.created_at || new Date().toISOString(),
    updatedAt: folder.updated_at || new Date().toISOString(),
  };
}


export interface ApiFileResponse {
  file_id: string;
  file_type: 'excel' | 'pdf';
  /** The file's one name, with its extension. The tree shows `fileStem(filename)`. */
  filename: string;
  storage_path: string;
  folder_id: string | null;
  agent_id: string;
  source: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The conversation that produced this file (generated files only — e.g. a
   *  built page). Used to resume the same conversation session from the viewer. */
  conversation_id?: string | null;
  /** Number of sheets the workbook's parse stored; null for a non-Excel file. */
  sheet_count?: number | null;
  processing_status?: {
    status: 'ingesting' | 'complete' | 'failed';
    units_total: number;
    units_ingested: number;
    unit_type: 'sheets' | 'pages' | 'slides';
    is_queryable: boolean;
    is_complete?: boolean;
    error?: string;
  } | null;
}

function mapApiFileToTreeItem(file: ApiFileResponse): TreeItem {
  return {
    id: file.file_id,
    name: fileStem(file.filename),
    filename: file.filename,
    type: 'file',
    parentId: file.folder_id,
    createdAt: file.created_at || new Date().toISOString(),
    updatedAt: file.updated_at || new Date().toISOString(),
    file_type: file.file_type,
    source: file.source ?? undefined,
  };
}

export interface CreateFileResponse {
  file_id: string;
  file_type: 'excel' | 'pdf' | 'powerpoint' | 'word' | 'text';
  upload_url: string;
  /** Sent verbatim with the PUT: signed into `upload_url`, they carry the
   *  object's custom metadata the ingest trigger resolves the row from. */
  upload_headers: Record<string, string>;
  storage_path: string;
}

function mapApiConversationToTreeItem(conversation: ApiConversation): TreeItem {
  return {
    id: conversation.id,
    name: conversation.name,
    type: 'conversation',
    parentId: null,
    createdAt: conversation.created_at || new Date().toISOString(),
    updatedAt: conversation.updated_at || new Date().toISOString(),
  };
}

export function mapApiProjectToProject(apiProject: ApiProject): Project {
  return {
    id: apiProject.id,
    name: apiProject.name,
    description: apiProject.description || '',
    industry: (apiProject.settings as Record<string, string>)?.industry || '',
    ownershipType: (apiProject.settings as Record<string, string>)?.ownershipType || '',
    createdAt: apiProject.created_at || new Date().toISOString(),
    updatedAt: apiProject.updated_at || new Date().toISOString(),
    role: apiProject.role ?? 'owner',
    memberCount: apiProject.member_count ?? 1,
    creator: {
      id: apiProject.creator?.id ?? null,
      displayName: apiProject.creator?.display_name ?? null,
      email: apiProject.creator?.email ?? null,
      photoUrl: apiProject.creator?.photo_url ?? null,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// HTTP Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (user) {
    // Send Firebase ID token for backend verification
    const token = await user.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * How long a read the first render waits on may take before it is given up.
 * A request that never answers (a stalled connection, a dev port held by a
 * stale relay) must end in an error the screen offers a retry for, never in
 * an eternal spinner. Only such gating reads pass it; a stream never does.
 */
export const LOAD_TIMEOUT_MS = 30_000;

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit & { timeoutMs?: number } = {}
): Promise<T> {
  const { timeoutMs, ...init } = options;
  const headers = await getAuthHeaders();

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...init,
      signal: timeoutMs ? AbortSignal.timeout(timeoutMs) : init.signal,
      headers: {
        ...headers,
        ...init.headers,
      },
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'TimeoutError') {
      throw new ApiError(0, 'The server did not answer in time');
    }
    throw e;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = errorData.detail ?? errorData.error;
    throw new ApiError(
      response.status,
      errorMessage(detail) || `Request failed: ${response.statusText}`,
      detail && typeof detail === 'object' && typeof detail.error === 'string' ? detail.error : undefined
    );
  }

  // Empty-body successes (204 No Content, e.g. DELETE) have no JSON to parse —
  // calling response.json() on them throws "Unexpected end of JSON input".
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** A FastAPI `detail` (the backend's global handler surfaces it as `error`)
 *  is a string, or a structured `{error, message}` the backend uses for
 *  sentences worth showing (an OAuth refusal, an expired connection). Either
 *  way the user sees the sentence, never "[object Object]". */
function errorMessage(detail: unknown): string | undefined {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && typeof (detail as { message?: unknown }).message === 'string') {
    return (detail as { message: string }).message;
  }
  return undefined;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** A structured detail's `error` — what the caller acts on, beside the sentence. */
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Projects API
// ─────────────────────────────────────────────────────────────────────────────

export interface CreateProjectParams {
  name: string;
  description?: string;
  settings?: {
    industry?: string;
    ownershipType?: string;
    [key: string]: unknown;
  };
}

export interface UpdateProjectParams {
  name?: string;
  description?: string;
  settings?: Record<string, unknown>;
}

export const projectsApi = {
  /** A project's recent work, across the agents the person reaches — Home's
   *  rows. A project has no tree: files are an agent's (`agentsApi.files`). */
  async recent(projectId: string): Promise<ProjectRecent> {
    return apiRequest<ProjectRecent>(`/api/projects/${projectId}/recent`);
  },

  /**
   * Create a new project.
   */
  async create(params: CreateProjectParams): Promise<Project> {
    const apiProject = await apiRequest<ApiProject>('/api/projects', {
      method: 'POST',
      body: JSON.stringify({
        name: params.name,
        description: params.description,
        settings: params.settings,
      }),
    });
    return mapApiProjectToProject(apiProject);
  },

  /**
   * Update an existing project.
   */
  async update(projectId: string, params: UpdateProjectParams): Promise<ApiProject> {
    return apiRequest<ApiProject>(`/api/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  /**
   * Delete a project.
   */
  async delete(projectId: string): Promise<void> {
    await apiRequest<{ deleted: boolean }>(`/api/projects/${projectId}`, {
      method: 'DELETE',
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Agents API — the actor within a project (docs/projects/index.md § The agent)
// ─────────────────────────────────────────────────────────────────────────────

/** An agent: grants and playbooks are its. A project has any
 *  number of them and no default (docs/projects/index.md § The agent). */
export interface Agent {
  id: string;
  project_id: string;
  name: string;
  description: string | null;
  settings: Record<string, unknown> | null;
  /** The person its routines run as (docs/projects/index.md § The sponsor). */
  sponsor_user_id?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UpdateAgentParams {
  name?: string;
  description?: string;
}

export const agentsApi = {
  /** An agent's tree — its folders and files. A tree never spans agents. */
  async files(agentId: string): Promise<AgentFiles> {
    return apiRequest<AgentFiles>(`/api/agents/${agentId}/files`);
  },

  /** The project's agents, as any member may see them. */
  async list(projectId: string): Promise<Agent[]> {
    const res = await apiRequest<Agent[] | { agents: Agent[] }>(`/api/projects/${projectId}/agents`);
    return Array.isArray(res) ? res : res.agents;
  },

  /** Create an agent in a project (owner). 409 on a duplicate name. */
  async create(projectId: string, params: { name: string; description?: string }): Promise<Agent> {
    return apiRequest<Agent>(`/api/projects/${projectId}/agents`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  },

  async get(agentId: string): Promise<Agent> {
    return apiRequest<Agent>(`/api/agents/${agentId}`);
  },

  /** Rename / redescribe (owner). */
  async update(agentId: string, params: UpdateAgentParams): Promise<Agent> {
    return apiRequest<Agent>(`/api/agents/${agentId}`, {
      method: 'PATCH',
      body: JSON.stringify(params),
    });
  },

  /** Delete an agent (owner). 409 for the default; cascades its grants. */
  async remove(agentId: string): Promise<void> {
    await apiRequest(`/api/agents/${agentId}`, { method: 'DELETE' });
  },

  /** The agent's grants — everyone who reaches the agent sees all of them. */
  async listGrants(agentId: string): Promise<AgentGrant[]> {
    return apiRequest(`/api/integrations/agents/${agentId}/grants`);
  },

  /** Attach an integration to an agent — the row is the grant; `config` is the provider's own settings. */
  async createGrant(agentId: string, integrationId: string, config: Record<string, unknown> = {}): Promise<AgentGrantRef> {
    return apiRequest(`/api/integrations/agents/${agentId}/grants`, {
      method: 'POST',
      body: JSON.stringify({ integration_id: integrationId, config }),
    });
  },

  /** Update a grant's config. */
  async updateGrant(agentId: string, grantId: string, config: Record<string, unknown>): Promise<AgentGrantRef> {
    return apiRequest(`/api/integrations/agents/${agentId}/grants/${grantId}`, {
      method: 'PATCH',
      body: JSON.stringify({ config }),
    });
  },

  /** Remove a grant. */
  async deleteGrant(agentId: string, grantId: string): Promise<void> {
    await apiRequest(`/api/integrations/agents/${agentId}/grants/${grantId}`, { method: 'DELETE' });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Folders API
// ─────────────────────────────────────────────────────────────────────────────

export interface CreateFolderParams {
  name: string;
  parent_folder_id?: string;
  /** The agent the folder is for. A parent decides on its own — a folder is
   *  its parent's agent's — so this matters at an agent's root. */
  agent_id?: string;
  integration_id?: string;
  provider_folder_id?: string;
}

export interface UpdateFolderParams {
  name?: string;
  parent_folder_id?: string;
  integration_id?: string;
  provider_folder_id?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Members API — the roster of a project (docs/projects/index.md § Members)
// ─────────────────────────────────────────────────────────────────────────────

interface ApiMemberAccess {
  agent_ids: string[];
  live_model_slugs: string[];
}

const toAccess = (a: ApiMemberAccess): MemberAccess => ({ agentIds: a.agent_ids, liveModelSlugs: a.live_model_slugs });
const fromAccess = (a: MemberAccess): ApiMemberAccess => ({ agent_ids: a.agentIds, live_model_slugs: a.liveModelSlugs });

interface ApiProjectMember {
  user_id: string;
  display_name: string | null;
  email: string | null;
  photo_url: string | null;
  role: ProjectRole;
  added_at: string | null;
  access: ApiMemberAccess | null;
}

export const membersApi = {
  /** The roster, as anyone in the project may see it, plus the caller's role. */
  async list(projectId: string): Promise<{ members: ProjectMember[]; role: ProjectRole }> {
    const res = await apiRequest<{ members: ApiProjectMember[]; role: ProjectRole }>(
      `/api/projects/${projectId}/members`,
    );
    return {
      role: res.role,
      members: res.members.map((m) => ({
        userId: m.user_id,
        displayName: m.display_name,
        email: m.email,
        photoUrl: m.photo_url,
        role: m.role,
        addedAt: m.added_at,
        access: m.access ? toAccess(m.access) : null,
      })),
    };
  },

  /** Replace a member's access (owner). What it no longer names, they no longer reach. */
  async setAccess(projectId: string, userId: string, access: MemberAccess): Promise<void> {
    await apiRequest(`/api/projects/${projectId}/members/${userId}/access`, {
      method: 'PUT',
      body: JSON.stringify(fromAccess(access)),
    });
  },

  /** Remove someone (owner), or leave (yourself) — the same door, both sides. */
  async remove(projectId: string, userId: string): Promise<void> {
    await apiRequest(`/api/projects/${projectId}/members/${userId}`, { method: 'DELETE' });
  },
};

interface ApiProjectInvite {
  id: string;
  email: string;
  token: string;
  expires_at: string;
  created_at: string;
  access: ApiMemberAccess;
}

const toInvite = (i: ApiProjectInvite): ProjectInvite => ({
  id: i.id,
  email: i.email,
  link: `${window.location.origin}/invite?token=${encodeURIComponent(i.token)}`,
  expiresAt: i.expires_at,
  createdAt: i.created_at,
  access: toAccess(i.access),
});

/** A project's pending invites — the owner's. A person is added only by an
 *  invite they accept, account or not (docs/projects/sharing.md § Adding a person). */
export const projectInvitesApi = {
  /** Pending and expired invites, with their links; empty for anyone but the owner. */
  async list(projectId: string): Promise<ProjectInvite[]> {
    const res = await apiRequest<{ invites: ApiProjectInvite[] }>(`/api/projects/${projectId}/invites`);
    return res.invites.map(toInvite);
  },

  /** Invite by email, giving what `access` names. Inviting the same address again keeps
   *  its link, renews it, and replaces what it gives. */
  async create(projectId: string, email: string, access: MemberAccess): Promise<ProjectInvite> {
    const res = await apiRequest<{ invite: ApiProjectInvite }>(`/api/projects/${projectId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, ...fromAccess(access) }),
    });
    return toInvite(res.invite);
  },

  /** Revoke: the link stops working at once. */
  async revoke(projectId: string, inviteId: string): Promise<void> {
    await apiRequest(`/api/projects/${projectId}/invites/${inviteId}`, { method: 'DELETE' });
  },
};

export const foldersApi = {
  /**
   * List all folders in a project.
   */
  async list(projectId: string): Promise<{ folders: TreeItem[] }> {
    const response = await apiRequest<ListFoldersResponse>(
      `/api/projects/${projectId}/folders`
    );

    return {
      folders: response.folders.map(mapApiFolderToTreeItem),
    };
  },

  /**
   * Get a single folder by ID.
   */
  async get(folderId: string): Promise<TreeItem> {
    const folder = await apiRequest<ApiFolder>(`/api/folders/${folderId}`);
    return mapApiFolderToTreeItem(folder);
  },

  /**
   * Create a new folder in an agent's tree (`params.agent_id`, or its parent's).
   */
  async create(projectId: string, params: CreateFolderParams): Promise<TreeItem> {
    const folder = await apiRequest<ApiFolder>(`/api/projects/${projectId}/folders`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return mapApiFolderToTreeItem(folder);
  },

  /**
   * Update an existing folder.
   */
  async update(folderId: string, params: UpdateFolderParams): Promise<TreeItem> {
    const folder = await apiRequest<ApiFolder>(`/api/folders/${folderId}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
    return mapApiFolderToTreeItem(folder);
  },

  /**
   * Delete a folder (cascades to children).
   */
  async delete(folderId: string): Promise<void> {
    await apiRequest<{ deleted: boolean }>(`/api/folders/${folderId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Rename a folder.
   * Convenience method that wraps update().
   */
  async rename(folderId: string, newName: string): Promise<TreeItem> {
    return this.update(folderId, { name: newName });
  },

  /**
   * Move a folder to a different parent (or root if empty string).
   * Convenience method that wraps update().
   */
  async move(folderId: string, newParentFolderId: string | null): Promise<TreeItem> {
    return this.update(folderId, { parent_folder_id: newParentFolderId ?? '' });
  },

  /**
   * Remove sync link from a folder. Folder becomes regular, files stay.
   */
  async unsync(folderId: string): Promise<TreeItem> {
    const folder = await apiRequest<ApiFolder>(`/api/folders/${folderId}/unsync`, {
      method: 'POST',
    });
    return mapApiFolderToTreeItem(folder);
  },

  /**
   * Trigger manual sync refresh for a synced folder.
   */
  async refresh(folderId: string): Promise<{ status: string }> {
    return apiRequest<{ status: string }>(`/api/folders/${folderId}/refresh`, {
      method: 'POST',
    });
  },
};

// For backwards compatibility, export treeItemsApi that delegates to type-specific APIs
export const treeItemsApi = {
  /**
   * Create a new folder in a project.
   */
  async createFolder(projectId: string, name: string, parentFolderId?: string): Promise<TreeItem> {
    return foldersApi.create(projectId, {
      name,
      parent_folder_id: parentFolderId,
    });
  },

  /**
   * Move a tree item to a different folder.
   * Routes to appropriate API based on item type. Conversations are not in the
   * tree — they are organized by recency, not by folder — so they never move.
   */
  async move(itemId: string, newParentFolderId: string | null, itemType?: TreeItemType): Promise<TreeItem> {
    if (itemType === 'file') {
      return filesApi.move(itemId, newParentFolderId);
    }
    return foldersApi.move(itemId, newParentFolderId);
  },

  /**
   * Rename a tree item.
   * Routes to appropriate API based on item type.
   */
  async rename(itemId: string, newName: string, itemType?: TreeItemType): Promise<TreeItem> {
    if (itemType === 'folder') {
      return foldersApi.rename(itemId, newName);
    } else if (itemType === 'file') {
      return filesApi.rename(itemId, newName);
    } else if (itemType === 'conversation') {
      return conversationsApi.rename(itemId, newName);
    }
    // If type unknown, try each in order
    try {
      return await foldersApi.rename(itemId, newName);
    } catch {
      return await conversationsApi.rename(itemId, newName);
    }
  },

  /**
   * Delete a tree item.
   * Routes to appropriate API based on item type.
   */
  async delete(itemId: string, itemType?: TreeItemType): Promise<void> {
    if (itemType === 'folder') {
      await foldersApi.delete(itemId);
      return;
    } else if (itemType === 'file') {
      await filesApi.delete(itemId);
      return;
    } else if (itemType === 'conversation') {
      await conversationsApi.delete(itemId);
      return;
    }
    // If type unknown, try each in order
    try {
      await foldersApi.delete(itemId);
    } catch {
      try {
        await filesApi.delete(itemId);
      } catch {
        await conversationsApi.delete(itemId);
      }
    }
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Conversations API
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiConversation {
  id: string;
  project_id: string;
  name: string;
  // 'auto_first_message' until the background titler runs, then 'auto_llm'.
  // 'user' once a manual rename happens. Used by the new-conversation poll
  // loop to know when to stop waiting for the LLM-generated title.
  // 'routine' on a routine run's conversation — named after the routine.
  title_source?: 'auto_first_message' | 'auto_llm' | 'user' | 'routine';
  /** Set on a routine run's conversation — readable by whoever reaches its agent. */
  routine_run_id?: string | null;
  /** The agent it speaks with; null on a conversation from before agents. */
  agent_id?: string | null;
  message_count?: number;
  last_message?: string;
  project_name?: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface ApiMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  parts: Record<string, unknown>[] | null;
  created_at: string | null;
}

interface ListConversationsResponse {
  conversations: ApiConversation[];
  total?: number;
  limit?: number;
  offset?: number;
  project_id?: string;
}

interface RecentConversationsResponse {
  conversations: ApiConversation[];
}

interface ConversationWithMessagesResponse extends ApiConversation {
  messages: ApiMessage[];
}

interface ListMessagesResponse {
  messages: ApiMessage[];
  total: number;
  limit: number;
  offset: number;
}

export interface UpdateConversationParams {
  name?: string;
}

export interface AddMessageParams {
  role: 'user' | 'assistant';
  content: string;
  parts?: Record<string, unknown>[];
}

/** How many of an agent's conversations one request asks for. */
const CONVERSATION_PAGE = 200;

export const conversationsApi = {
  /**
   * List conversations for the current user.
   * Can optionally filter by project_id.
   */
  async list(options?: {
    projectId?: string;
    agentId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ conversations: ApiConversation[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.projectId) params.append('project_id', options.projectId);
    if (options?.agentId) params.append('agent_id', options.agentId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const queryString = params.toString();
    const endpoint = `/api/conversations${queryString ? `?${queryString}` : ''}`;

    const response = await apiRequest<ListConversationsResponse>(endpoint);

    return {
      conversations: response.conversations,
      total: response.total ?? response.conversations.length,
    };
  },

  /**
   * Every one of my conversations with an agent. A conversation is mine, so
   * it is reached through my own collection, narrowed by the agent — never
   * through a route under the agent.
   */
  async forAgent(agentId: string): Promise<ConversationRow[]> {
    const rows: ConversationRow[] = [];
    for (;;) {
      const page = await this.list({ agentId, limit: CONVERSATION_PAGE, offset: rows.length });
      rows.push(...page.conversations);
      if (page.conversations.length < CONVERSATION_PAGE || rows.length >= page.total) return rows;
    }
  },

  /**
   * Get recent conversations across all projects for sidebar display.
   */
  async getRecent(limit = 10): Promise<ApiConversation[]> {
    const response = await apiRequest<RecentConversationsResponse>(
      `/api/conversations/recent?limit=${limit}`
    );
    return response.conversations;
  },

  /**
   * Get a single conversation with messages.
   */
  async get(
    conversationId: string,
    options?: { includeMessages?: boolean; messageLimit?: number }
  ): Promise<ConversationWithMessagesResponse> {
    const params = new URLSearchParams();
    if (options?.includeMessages !== undefined) {
      params.append('include_messages', options.includeMessages.toString());
    }
    if (options?.messageLimit) {
      params.append('message_limit', options.messageLimit.toString());
    }

    const queryString = params.toString();
    const endpoint = `/api/conversations/${conversationId}${queryString ? `?${queryString}` : ''}`;

    return apiRequest<ConversationWithMessagesResponse>(endpoint);
  },

  /**
   * Stop the in-flight assistant turn for a conversation. The turn keeps
   * running on a plain disconnect; this is the only stop signal. 404 means
   * there is no active run (already finished).
   */
  async cancel(conversationId: string): Promise<void> {
    await apiRequest<{ status: string }>(
      `/api/conversations/${conversationId}/cancel`,
      { method: 'POST' }
    );
  },

  /**
   * Update a conversation.
   */
  async update(
    conversationId: string,
    params: UpdateConversationParams
  ): Promise<ApiConversation> {
    return apiRequest<ApiConversation>(`/api/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  /**
   * Delete a conversation.
   */
  async delete(conversationId: string): Promise<void> {
    await apiRequest<{ deleted: boolean }>(`/api/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get messages for a conversation (paginated).
   */
  async getMessages(
    conversationId: string,
    options?: { limit?: number; offset?: number }
  ): Promise<{ messages: ApiMessage[]; total: number }> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const queryString = params.toString();
    const endpoint = `/api/conversations/${conversationId}/messages${queryString ? `?${queryString}` : ''}`;

    const response = await apiRequest<ListMessagesResponse>(endpoint);

    return {
      messages: response.messages,
      total: response.total,
    };
  },

  /**
   * Add a message to a conversation.
   */
  async addMessage(
    conversationId: string,
    params: AddMessageParams
  ): Promise<ApiMessage> {
    return apiRequest<ApiMessage>(
      `/api/conversations/${conversationId}/messages`,
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
  },

  /**
   * Rename a conversation.
   */
  async rename(conversationId: string, newName: string): Promise<TreeItem> {
    const conv = await this.update(conversationId, { name: newName });
    return mapApiConversationToTreeItem(conv);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Users API
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiUser {
  id: string;
  email: string;
  display_name: string | null;
  photo_url: string | null;
  preferences: UserPreferences;
  created_at: string | null;
}

export interface TabData {
  id: string;
  itemId: string;
  /** Persisted as a plain string, so widening the union leaves every tab
   *  already in `tabsByProject` parsing exactly as before. */
  itemType: TabItemType;
  title: string;
  isActive: boolean;
  isDraft?: boolean;
  storeKey?: string;
}

export interface ProjectTabState {
  tabs: TabData[];
  activeTabId?: string;
}

export interface UserPreferences {
  activeProjectId?: string;
  tabsByProject?: Record<string, ProjectTabState>;  // projectId -> tabs state
  expandedFolders?: Record<string, string[]>;  // projectId -> folder IDs
  panes?: Record<string, PaneState>;  // pane key -> width + collapsed
  /** The agent drawer: open or not, and the conversation it holds per agent. */
  drawer?: { open?: boolean; threads?: Record<string, string> };
  /** The sidebar's Recent group, newest first (`stores/recentStore.ts`). */
  recent?: RecentItem[];
  /** What this person pinned to the sidebar, in their order (`stores/pinStore.ts`). */
  pins?: PinItem[];
  /** Set once the first-load seed ran, before seeding was per project; read only to
   *  carry that over to `pinsSeededFor` (`stores/pinStore.ts`). */
  pinsSeeded?: boolean;
  /** The projects whose agents (and live models) were pinned the first time the person opened
   *  them, so an emptied group stays empty (`stores/pinStore.ts`). */
  pinsSeededFor?: string[];
  /** The frame's geometry (`stores/frameStore.ts`): the sidebar folded to its
   *  rail, and the width the person dragged the agent drawer to. */
  frame?: { sidebarCollapsed?: boolean; drawerWidth?: number };
}

/** One pin in the sidebar: it lists under the group of its kind. */
export interface PinItem {
  kind: 'page' | 'live-model' | 'routine' | 'agent';
  /** Stable identity: the page's file id, `projectId/slug` for a live model (a
   *  live model is its project's), the routine's id, the agent's id. */
  id: string;
  title: string;
  /** Where it opens. */
  path: string;
  agentId?: string | null;
}

/** One open item in the sidebar's Recent group. */
export interface RecentItem {
  kind: 'conversation' | 'file' | 'presentation' | 'routine' | 'run' | 'live-model' | 'page';
  id: string;
  title: string;
  /** Where it opens. */
  path: string;
  /** The agent it belongs to, when it is one agent's. */
  agentId?: string | null;
}

/** One resizable pane's remembered geometry (`hooks/useResizablePane.ts`). */
export interface PaneState {
  width: number;
  collapsed: boolean;
}

export const usersApi = {
  /**
   * The first load: projects, every project's agents, preferences and flags,
   * in one request. Carries no project's contents.
   */
  async start(): Promise<StartResponse> {
    return apiRequest<StartResponse>('/api/users/me/start', { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /**
   * Get the current authenticated user's info.
   * Returns 404 if user doesn't exist in the database (signed in but no account).
   */
  async me(): Promise<ApiUser> {
    return apiRequest<ApiUser>('/api/users/me');
  },

  /**
   * Update the current user's preferences.
   * Merges with existing preferences (shallow merge).
   * To remove a key, set its value to null.
   */
  async updatePreferences(preferences: Partial<UserPreferences>): Promise<{ preferences: UserPreferences }> {
    return apiRequest<{ preferences: UserPreferences }>('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ preferences }),
    });
  },

  /**
   * Set the current user's display name — the name co-members see beside a
   * shared project, in the roster, and in the switcher.
   *
   * Pass an empty string to clear it and go back to being shown as your email.
   * Sent as an explicit null so the server can tell "clear this" apart from
   * "leave it alone", which is what omitting the key means.
   */
  async updateDisplayName(displayName: string): Promise<{ display_name: string | null }> {
    return apiRequest<{ display_name: string | null }>('/api/users/me', {
      method: 'PATCH',
      body: JSON.stringify({ display_name: displayName.trim() || null }),
    });
  },

  /**
   * Delete the current user's account and everything only theirs. What they
   * made in other people's projects stays with those projects' owners.
   * The sign-in identity is not the server's to delete — see
   * `AuthContext.deleteAccount`.
   */
  async deleteMe(): Promise<{ deleted: boolean }> {
    return apiRequest<{ deleted: boolean }>('/api/users/me', { method: 'DELETE' });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Invites API
// ─────────────────────────────────────────────────────────────────────────────

export interface InviteValidationResponse {
  valid: boolean;
  email?: string;
  /** Set on a project invite: what the link was sent to say — what it gives
   *  (agents by name, live models by count), then where. */
  project_name?: string;
  invited_by?: string | null;
  agent_names?: string[];
  live_model_count?: number;
  reason?: 'expired' | 'used' | 'not_found';
}

export interface InviteAcceptResponse {
  success: boolean;
  new_user?: boolean;
  /** Set on a project invite: the project just joined, now the one shown. */
  project_id?: string;
  error?: string;
}

export const invitesApi = {
  /**
   * Validate an invite token (public endpoint, no auth required).
   */
  async validate(token: string): Promise<InviteValidationResponse> {
    // No auth needed for validation
    const response = await fetch(`${API_BASE_URL}/api/invites/validate?token=${encodeURIComponent(token)}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || `Request failed: ${response.statusText}`
      );
    }
    return response.json();
  },

  /**
   * Accept an invite (requires Firebase auth).
   */
  async accept(token: string): Promise<InviteAcceptResponse> {
    return apiRequest<InviteAcceptResponse>('/api/invites/accept', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Files API
// ─────────────────────────────────────────────────────────────────────────────

/** A citation address — content-addressed locator the viewer resolves to a passage. */
export interface ApiCitationAnchor {
  format: 'pdf' | 'pptx' | 'xlsx';
  page?: number | null;
  slide?: number | null;
  sheet?: string | null;
  quote?: { exact: string; prefix?: string; suffix?: string } | null;
  ref?: string | null;
}

/** One typed, display-ready metadata field (kind-agnostic). */
export interface ApiFileMetadataField {
  key: string;
  label: string;
  value: unknown;
  type: 'string' | 'date' | 'currency' | 'number' | 'object';
  citation: ApiCitationAnchor | null;
}

export interface ApiFileMetadata {
  kind: string;
  schema_version: number;
  extracted_at: string | null;
  fields: ApiFileMetadataField[];
}

export const filesApi = {
  /**
   * Create a new file record for an agent and get a signed upload URL. The
   * file is the agent's: `folderId`'s when given, else `agentId`'s.
   */
  async create(
    filename: string, projectId: string, agentId: string, folderId?: string | null,
  ): Promise<CreateFileResponse> {
    return apiRequest<CreateFileResponse>('/api/files', {
      method: 'POST',
      body: JSON.stringify({
        filename,
        project_id: projectId,
        agent_id: agentId,
        folder_id: folderId ?? undefined,
      }),
    });
  },

  async get(fileId: string): Promise<ApiFileResponse> {
    return apiRequest<ApiFileResponse>(`/api/files/${fileId}`);
  },

  /** `name` is the stem — the server holds the extension fixed. */
  async update(fileId: string, params: { name?: string; folder_id?: string }): Promise<ApiFileResponse> {
    return apiRequest<ApiFileResponse>(`/api/files/${fileId}`, {
      method: 'PUT',
      body: JSON.stringify(params),
    });
  },

  async delete(fileId: string): Promise<void> {
    await apiRequest<{ deleted: boolean }>(`/api/files/${fileId}`, {
      method: 'DELETE',
    });
  },

  async rename(fileId: string, newName: string): Promise<TreeItem> {
    const file = await this.update(fileId, { name: newName });
    return mapApiFileToTreeItem(file);
  },

  async move(fileId: string, newFolderId: string | null): Promise<TreeItem> {
    const file = await this.update(fileId, { folder_id: newFolderId ?? '' });
    return mapApiFileToTreeItem(file);
  },

  async download(fileId: string): Promise<{ download_url: string; filename: string; download_filename: string }> {
    return apiRequest<{ download_url: string; filename: string; download_filename: string }>(`/api/files/${fileId}/download`);
  },

  async getUploadUrl(fileId: string): Promise<{ upload_url: string; upload_headers: Record<string, string> }> {
    return apiRequest<{ upload_url: string; upload_headers: Record<string, string> }>(`/api/files/${fileId}/upload-url`, {
      method: 'POST',
    });
  },

  /**
   * Cached structured metadata for a file — one entry per active `kind` — or `null`
   * when none exists (204). Read-only — never triggers extraction. Only call when the
   * tree's `has_metadata` signal is true, so this is never used as a probe.
   * (The shared `apiRequest` helper calls `response.json()` unconditionally, so the
   * empty 204 body is handled here directly.)
   */
  async getMetadata(fileId: string): Promise<ApiFileMetadata[] | null> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/files/${fileId}/metadata`, { headers });
    if (response.status === 204) return null;
    if (!response.ok) {
      throw new ApiError(response.status, `Request failed: ${response.statusText}`);
    }
    return response.json();
  },

};

// ─────────────────────────────────────────────────────────────────────────────
// Brand Templates API (Settings → Templates)
// ─────────────────────────────────────────────────────────────────────────────

export type BrandTemplateStatus = 'uploading' | 'parsing' | 'ready' | 'failed';

export interface BrandTemplate {
  template_id: string;
  filename: string;
  display_name: string | null;
  status: BrandTemplateStatus;
  error: string | null;
  is_default: boolean;
  created_at: string | null;
}

export interface CreateTemplateResponse {
  template_id: string;
  upload_url: string;
  storage_path: string;
}

export interface TemplateKeepEntry {
  var: string | null;
  shape: string | null;
  fillable: boolean;
}

export interface TemplateRolePreview {
  role: string;
  slide_idx: number | null;
  desc: string | null;
  keep: TemplateKeepEntry[];
  image_url: string | null;
}

export interface TemplatePaletteRoles {
  primary?: string | null;
  secondary?: string | null;
  accents?: string[];
  neutrals?: string[];
}

export interface TemplatePreview {
  roles: TemplateRolePreview[];
  palette: {
    brand_colors: string[];
    palette_roles: TemplatePaletteRoles | null;
    theme_palette: Record<string, string>;
  };
  fonts: { title?: string | null; body?: string | null };
  table: Record<string, unknown>;
  style_notes: string | null;
  chart: string | null;
  header_footer: string | null;
  kpi_styles: string[];
}

export const templatesApi = {
  async list(): Promise<{ templates: BrandTemplate[]; default_template_id: string | null }> {
    return apiRequest('/api/templates');
  },

  async create(filename: string, displayName?: string, contentType?: string): Promise<CreateTemplateResponse> {
    return apiRequest<CreateTemplateResponse>('/api/templates', {
      method: 'POST',
      body: JSON.stringify({
        filename,
        display_name: displayName ?? undefined,
        content_type: contentType ?? undefined,
      }),
    });
  },

  /** Kick off the background parse once the .pptx has finished uploading. */
  async parse(templateId: string): Promise<{ status: string }> {
    return apiRequest(`/api/templates/${templateId}/parse`, { method: 'POST' });
  },

  /** What the factory extracted: role picks + keep-maps, palette, styling. */
  async preview(templateId: string): Promise<TemplatePreview> {
    return apiRequest(`/api/templates/${templateId}/preview`);
  },

  /** Override the extracted palette / table header on a parsed template.
   * `tableHeaderFill: ''` clears the override (reverts to the black default). */
  async updatePalette(
    templateId: string,
    patch: { brand_colors?: string[]; table_header_fill?: string },
  ): Promise<{ brand_colors: string[]; table: Record<string, unknown> }> {
    return apiRequest(`/api/templates/${templateId}/palette`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },

  async setDefault(templateId: string): Promise<{ default_brand_template_id: string | null }> {
    return apiRequest(`/api/templates/${templateId}/set-default`, { method: 'POST' });
  },

  async delete(templateId: string): Promise<void> {
    await apiRequest<void>(`/api/templates/${templateId}`, { method: 'DELETE' });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Citations API (pointer-based provenance)
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiCitation {
  id: string;
  /** The project file it points into — or, instead, a version of the agent's own work. */
  file_id: string | null;
  work_id?: string | null;
  work_version?: number | null;
  file_name: string;
  file_type: string;
  location: Record<string, any> | null;
  description: string | null;
  data: string | null;
  created_at: string | null;
  updated_at?: string | null;
}

export const citationsApi = {
  /**
   * Get citation details for tooltip display.
   * Returns file name, file type, and location for hover tooltips.
   */
  async get(citationId: string): Promise<ApiCitation> {
    return apiRequest<ApiCitation>(`/api/citations/${citationId}`);
  },

  /**
   * List all citations for a conversation.
   */
  async list(conversationId: string): Promise<{ citations: ApiCitation[]; count: number }> {
    return apiRequest<{ citations: ApiCitation[]; count: number }>(
      `/api/citations?conversation_id=${encodeURIComponent(conversationId)}`
    );
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// The first load, and a project's contents
// ─────────────────────────────────────────────────────────────────────────────

/** A folder row in an agent's tree. */
export interface FolderRow {
  id: string;
  /** The agent whose folder it is — a folder and everything in it is one
   *  agent's (docs/projects/index.md § What an agent reads). */
  agent_id: string;
  name: string;
  parent_folder_id: string | null;
  sync_status?: string;
  integration_id?: string | null;
  provider_folder_id?: string | null;
  last_synced_at?: string | null;
  sync_error?: string | null;
  sync_skipped?: SyncSkip[];
  created_at: string | null;
  updated_at: string | null;
}

/** A file row in an agent's tree. */
export interface FileRow {
  id: string;
  file_type: 'excel' | 'pdf' | 'powerpoint' | 'word' | 'text';
  /** The file's one name, with its extension. The tree shows `fileStem(filename)`. */
  filename: string;
  folder_id: string | null;
  /** The agent whose file it is; only that agent's turns read it. */
  agent_id: string;

  source: string | null;
  /** A delivered file's version of origin (`source = 'delivered'`). */
  delivered_from?: { work_id: string; version: number | null; path: string | null } | null;
  created_at: string | null;
  updated_at: string | null;
  /** True when the file has cached structured metadata — gates the inspection panel. */
  has_metadata?: boolean;
  processing_status?: {
    status: 'ingesting' | 'complete' | 'failed';
    units_total: number;
    units_ingested: number;
    unit_type: 'sheets' | 'pages' | 'slides';
    is_queryable: boolean;
    is_complete: boolean;
    error?: string;
  } | null;
  // Generated decks surface as powerpoint files. A just-built deck opens from
  // its conversation card by presentation_id until the worker marks the file complete.
}

/** One of my conversations, as a list returns it. */
export interface ConversationRow {
  id: string;
  name: string;
  /** Set on a routine run's conversation (docs/conversations/index.md). */
  routine_run_id?: string | null;
  /** The agent it speaks with; null on a conversation from before agents. */
  agent_id?: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** An agent's tree (`agentsApi.files`): its folders and its files. */
export interface AgentFiles {
  folders: FolderRow[];
  files: FileRow[];
}

/** A project's recent work — Home's rows, the newest of each kind
 *  (`projectsApi.recent`). */
export interface ProjectRecent {
  files: FileRow[];
  conversations: ConversationRow[];
}

/**
 * The signed-in user's first load (`usersApi.start`): what they reach and how
 * they left it. Nothing of any one project's contents — its recent work
 * (`projectsApi.recent`) and each agent's files (`agentsApi.files`) are asked
 * once the client knows what it shows.
 */
export interface StartResponse {
  projects: ApiProject[];
  /** Every project's agents, as `GET /api/projects/{id}/agents` lists them. */
  agents?: Record<string, Agent[]>;
  userPreferences: UserPreferences;
  flags: Record<string, boolean>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Integrations API (Google Drive, etc.)
// ─────────────────────────────────────────────────────────────────────────────

export interface Integration {
  id: string;
  provider: string;
  account_email: string | null;
  /** The connection's user-facing name (Salesforce: "acme (production)" — the
   *  org id in account_email is the merge key, never the label). Falls back to
   *  account_email server-side. */
  display_name?: string | null;
  status: string;
  scopes: string[] | null;
  connected_at: string;
}

/** The grant row as written — what create/update hand back. The row is the
 *  agent's (`agent_integrations`); the project is reached through the agent. */
export interface AgentGrantRef {
  id: string;
  agent_id: string;
  integration_id: string;
  config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * A grant as *listed*, self-describing: `provider`/`status` are joined in so a
 * member can render a row for a connection they do not own, and
 * `granted_by_user_id` says who provided it (resolve against the roster).
 * `config` is verbatim for the granter and redacted for everyone else —
 * what the grant does, not the handles it does it with.
 */
export interface AgentGrant extends AgentGrantRef {
  granted_by_user_id: string;
  provider: string | null;
  status: string | null;
}

export interface CredentialField {
  key: string;
  label: string;
  placeholder?: string;
  type?: 'text' | 'password';
  optional?: boolean;
}

export interface ProviderCatalogEntry {
  provider: string;
  label: string;
  tagline: string;
  project_blurb: string;
  /** Connected: how a second account of this provider is added, when the
   *  provider's own flow makes that non-obvious (QuickBooks: one company
   *  per consent). Empty when there is nothing to say. */
  connect_more: string;
  connect_mode: 'oauth' | 'credential' | 'admin';
  /** Present only for connect_mode==='credential': the form spec to render. */
  credential_fields?: CredentialField[] | null;
  /** What a grant on this provider lets the agent do, as one sentence. */
  access: string;
}

/** One queryable record type on a data-source connection, with its live
 *  accessibility (null = unknown / not probed). */
export interface EntityInfo {
  name: string;
  accessible: boolean | null;
  count: number | null;
}

export interface DriveItem {
  id: string;
  name: string;
  mime_type: string;
  is_folder: boolean;
  /** Whether the person can add files to this folder (the provider's answer
   *  for them). Null for files. */
  writable: boolean | null;
  size_bytes: number | null;
  modified_time: string | null;
}

/** A browse origin — one entry in the picker's left pane. Drive's storage is
 *  several disjoint trees; `id` is what browseFiles takes for each. */
export interface DriveRoot {
  id: string;
  name: string;
  kind: 'my_drive' | 'shared_drive' | 'shared_with_me';
  /** Whether the person can add files at the top of this root. */
  writable: boolean;
}

export interface ImportResult {
  imported: Array<{
    file_id: string;
    filename: string;
    file_type: string;
    source_id: string;
  }>;
  count: number;
}

export const integrationsApi = {
  /** Start OAuth flow — returns URL to redirect user to.
   *  Pass integrationId when reconnecting an expired integration.
   *  Pass writeAccess to request the drive.file write scope union (export setup).
   *  extraParams carries provider-specific pre-authorize choices (Salesforce:
   *  environment / login_host). */
  async authorize(provider: string, integrationId?: string, writeAccess?: boolean, extraParams?: Record<string, string>): Promise<{ authorize_url: string; state: string; integration_id: string | null }> {
    const params = new URLSearchParams();
    if (integrationId) params.set('integration_id', integrationId);
    if (writeAccess) params.set('write_access', 'true');
    for (const [k, v] of Object.entries(extraParams || {})) params.set(k, v);
    const qs = params.toString();
    return apiRequest(`/api/integrations/${provider}/authorize${qs ? `?${qs}` : ''}`, { method: 'POST' });
  },

  /** Create an app-owned export workbook. Pass parentId (a folder chosen in
   *  the storage picker) to create it there; omit it to land in My Drive root. */
  async createWorkbook(integrationId: string, name: string, sheet?: string, parentId?: string): Promise<{ file_id: string; name: string; sheet: string; url: string }> {
    return apiRequest(`/api/integrations/${integrationId}/workbook`, {
      method: 'POST',
      body: JSON.stringify({ name, sheet, parent_id: parentId }),
    });
  },

  /** Mint a short-lived session for the browser Google Picker: an OAuth token
   *  (off the stored grant), the browser API key, and the app id (project number). */
  async pickerSession(integrationId: string): Promise<{ access_token: string; api_key: string; app_id: string }> {
    return apiRequest(`/api/integrations/${integrationId}/picker-session`, { method: 'POST' });
  },

  /** Relay the provider's redirect to the backend callback (the code
   *  exchange). `redirectParams` is the redirect's query string, forwarded
   *  verbatim — the relay is a pipe: the endpoint declares what it needs
   *  (code/state, QuickBooks' realmId, a provider's ?error=) and the client
   *  has no opinion. `integration_id` is appended on reconnect — ours, not
   *  the provider's (docs/integrations/index.md § The connection's lifecycle). */
  async callback(provider: string, redirectParams: URLSearchParams, integrationId?: string): Promise<Integration> {
    const params = new URLSearchParams(redirectParams);
    if (integrationId) params.set('integration_id', integrationId);
    return apiRequest(`/api/integrations/${provider}/callback?${params}`);
  },

  /** List all integrations for the current user. */
  async list(): Promise<Integration[]> {
    return apiRequest('/api/integrations');
  },

  /** Get a specific integration. */
  async get(integrationId: string): Promise<Integration> {
    return apiRequest(`/api/integrations/${integrationId}`);
  },

  /** Connect (or update) a credential-based integration by pasting its fields.
   *  Validated against the live account before persist; 400 on bad credentials,
   *  503 if the account is transiently unreachable. Pass integrationId to
   *  re-paste onto a specific existing row (reconnect). */
  async connect(provider: string, fields: Record<string, string>, integrationId?: string): Promise<Integration> {
    const qs = integrationId ? `?integration_id=${encodeURIComponent(integrationId)}` : '';
    return apiRequest(`/api/integrations/${provider}/connect${qs}`, {
      method: 'POST',
      body: JSON.stringify(fields),
    });
  },

  /** List the record types a data-source connection exposes (the catalog).
   *  Pass probe entities to also get a live accessibility check per entity —
   *  used to gate adding an entity to a project's NetSuite allowlist. */
  async listEntities(integrationId: string, probe?: string[]): Promise<EntityInfo[]> {
    const qs = probe && probe.length ? `?probe=${encodeURIComponent(probe.join(','))}` : '';
    const resp = await apiRequest<{ entities: EntityInfo[] }>(`/api/integrations/${integrationId}/entities${qs}`);
    return resp.entities;
  },

  /** Exercise the stored credential once; a refused one comes back marked
   *  expired. What the QuickBooks disconnect page asks — the visit itself
   *  proves nothing. */
  async check(integrationId: string): Promise<Integration> {
    return apiRequest(`/api/integrations/${integrationId}/check`, { method: 'POST' });
  },

  /** Disconnect an integration — revoked at the provider first where it can be. */
  async disconnect(integrationId: string): Promise<void> {
    await apiRequest(`/api/integrations/${integrationId}`, { method: 'DELETE' });
  },

  /** List the provider's browse origins (My Drive, each Shared Drive,
   *  Shared with me) — the picker's left pane. */
  async listRoots(integrationId: string): Promise<DriveRoot[]> {
    const resp = await apiRequest<{ roots: DriveRoot[] }>(`/api/integrations/${integrationId}/roots`);
    return resp.roots;
  },

  /** Browse files in a provider folder. */
  async browseFiles(
    integrationId: string,
    folderId: string = 'root',
    pageToken?: string
  ): Promise<{ items: DriveItem[]; next_page_token: string | null }> {
    const params = new URLSearchParams({ folder_id: folderId });
    if (pageToken) params.set('page_token', pageToken);
    return apiRequest(`/api/integrations/${integrationId}/files?${params}`);
  },

  /** Import selected files into an agent's files, at its root. */
  async importFiles(
    integrationId: string,
    projectId: string,
    agentId: string,
    fileIds: string[]
  ): Promise<ImportResult> {
    return apiRequest(`/api/integrations/${integrationId}/files/import`, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, agent_id: agentId, file_ids: fileIds }),
    });
  },

  /** List Slack channels available to a bot integration. */
  async listChannels(integrationId: string): Promise<{ id: string; name: string; is_private: boolean }[]> {
    return apiRequest(`/api/integrations/${integrationId}/channels`);
  },

  /** The notification category catalog — single source of truth for the settings checklist. */
  async listNotifyCategories(): Promise<{ key: string; label: string; description: string; default_mode: 'realtime' | 'off' }[]> {
    return apiRequest(`/api/integrations/notify-categories`);
  },

  /** The provider catalog — what each integration is and what attaching it to a project allows.
   *  Shared by Settings and /projects; mirrors the backend registry. */
  async listCatalog(): Promise<ProviderCatalogEntry[]> {
    const resp = await apiRequest<{ providers: ProviderCatalogEntry[] }>(`/api/integrations/catalog`);
    return resp.providers;
  },
};

/** The grant verbs, bound to one actor. */
export interface GrantRoutes {
  list(): Promise<AgentGrant[]>;
  create(integrationId: string, config?: Record<string, unknown>): Promise<AgentGrantRef>;
  update(grantId: string, config: Record<string, unknown>): Promise<AgentGrantRef>;
  remove(grantId: string): Promise<void>;
}

/** The grant verbs, bound to the agent whose grants they are. */
export function grantsApiFor(agentId: string): GrantRoutes {
  return {
    list: () => agentsApi.listGrants(agentId),
    create: (integrationId, config = {}) => agentsApi.createGrant(agentId, integrationId, config),
    update: (grantId, config) => agentsApi.updateGrant(agentId, grantId, config),
    remove: (grantId) => agentsApi.deleteGrant(agentId, grantId),
  };
}


// ─────────────────────────────────────────────────────────────────────────────
// Presentations (PowerPoint decks) API
// ─────────────────────────────────────────────────────────────────────────────

export interface ApiPresentation {
  id: string;
  name: string | null;
  prompt: string;
  status: 'generating' | 'complete' | 'failed';
  error_message: string | null;
  /** Backing files row UUID (the .pptx binary lives there). */
  file_id: string | null;
  /** GCS path of the PDF rendition used for in-app preview. */
  rendition_pdf_path: string | null;
  has_preview: boolean;
  slide_count: number | null;
  /** Title-only outline reconstructed from per-slide rows. */
  slide_outline: { title: string }[];
  source_file_ids: string[];
  conversation_id: string | null;
  is_pinned: boolean;
  project_id: string;
  folder_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListPresentationsResponse {
  presentations: ApiPresentation[];
  total: number;
}

/** One resolved source behind a cited figure (chart/table/image). */
export interface ApiCitationSource {
  file_id: string | null;
  file_name: string | null;
  file_type: string | null;
  /** Pre-formatted location (e.g. "P&L, B5" / "Page 7" / "Slide 2"). */
  location: string;
  /** The cited value/excerpt, if the citation captured one. */
  value: string | null;
  description: string | null;
}

/** A cited figure on a slide, with its EMU bbox (left/top/width/height). */
export interface ApiCitationComponent {
  type: 'chart' | 'table' | 'image';
  /** EMU bbox; null when the extractor didn't capture a position. */
  bbox: { x: number; y: number; w: number; h: number } | null;
  sources: ApiCitationSource[];
}

export interface ApiPresentationCitations {
  slides: { slide_index: number; components: ApiCitationComponent[] }[];
}

export interface PresentationDownloadResult {
  blob: Blob;
  filename: string;
}

export const presentationsApi = {
  async get(presentationId: string): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(`/api/presentations/${presentationId}`);
  },

  /**
   * Resolve a PowerPoint file_id to its presentation row. Used when the
   * workspace shell opens a PPTX from the file tree (which only carries
   * the file_id, not the presentation_id).
   */
  async getByFileId(fileId: string): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(`/api/presentations/by-file/${fileId}`);
  },

  /**
   * Per-slide cited figures (chart/table/image) with EMU bbox + resolved
   * source(s). Feeds the source-on-hover overlay in PresentationViewer.
   */
  async citations(presentationId: string): Promise<ApiPresentationCitations> {
    return apiRequest<ApiPresentationCitations>(
      `/api/presentations/${presentationId}/citations`,
    );
  },

  async list(
    projectId: string,
    limit = 50,
    offset = 0,
  ): Promise<{ presentations: ApiPresentation[]; total: number }> {
    const r = await apiRequest<ListPresentationsResponse>(
      `/api/presentations?project_id=${projectId}&limit=${limit}&offset=${offset}`,
    );
    return { presentations: r.presentations, total: r.total };
  },

  /**
   * Mint a short-lived signed URL for the deck's PDF rendition. Returns a
   * same-origin URL with the friendly filename in the path and an HMAC token
   * in the query string. Drop it straight into <iframe src> — the browser
   * PDF viewer's "Save As" then uses the path-derived filename rather than
   * an opaque blob: identifier.
   */
  async previewUrl(
    presentationId: string,
  ): Promise<{ url: string; filename: string; expiresIn: number }> {
    const resp = await apiRequest<{
      url: string;
      filename: string;
      expires_in: number;
    }>(`/api/presentations/${presentationId}/preview-url`);
    return {
      url: `${API_BASE_URL}${resp.url}`,
      filename: resp.filename,
      expiresIn: resp.expires_in,
    };
  },

  /**
   * Fetch the deck binary through the backend (same-origin, auth-bearing).
   * Filename comes from X-Filename header (RFC 5987 quoted) so non-ASCII
   * characters round-trip correctly.
   */
  async download(presentationId: string): Promise<PresentationDownloadResult> {
    const headers = await getAuthHeaders();
    const resp = await fetch(
      `${API_BASE_URL}/api/presentations/${presentationId}/download`,
      { headers },
    );
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Download failed: ${resp.status} ${body || resp.statusText}`);
    }
    const blob = await resp.blob();
    let filename = 'deck.pptx';
    const hinted = resp.headers.get('X-Filename');
    if (hinted) {
      try {
        filename = decodeURIComponent(hinted);
      } catch {
        filename = hinted;
      }
    }
    return { blob, filename };
  },

  async update(
    presentationId: string,
    patch: { name?: string; folderId?: string },
  ): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(`/api/presentations/${presentationId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: patch.name,
        folder_id: patch.folderId,
      }),
    });
  },

  async togglePin(presentationId: string): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(`/api/presentations/${presentationId}/pin`, {
      method: 'POST',
    });
  },

  async delete(presentationId: string): Promise<void> {
    await apiRequest<{ deleted: boolean }>(`/api/presentations/${presentationId}`, {
      method: 'DELETE',
    });
  },

  // ── Direct-ops (no LLM) — fast, deterministic edits ──

  /** Rename a single slide's title. Backend re-renders the .pptx + .pdf. */
  async renameSlide(
    presentationId: string,
    slideIndex: number,           // 0-based
    newTitle: string,
  ): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(
      `/api/presentations/${presentationId}/operations/rename`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slide_index: slideIndex, new_title: newTitle }),
      },
    );
  },

  /** Drop a slide from the deck. Backend re-renders. */
  async deleteSlide(
    presentationId: string,
    slideIndex: number,
  ): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(
      `/api/presentations/${presentationId}/operations/delete`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slide_index: slideIndex }),
      },
    );
  },

  /** Move a slide: pop from fromIndex, insert at toIndex. Backend re-renders. */
  async moveSlide(
    presentationId: string,
    fromIndex: number,
    toIndex: number,
  ): Promise<ApiPresentation> {
    return apiRequest<ApiPresentation>(
      `/api/presentations/${presentationId}/operations/move`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_index: fromIndex, to_index: toIndex }),
      },
    );
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Work API — what an agent delivered: its own, versioned, never a file (docs/work.md)
// ─────────────────────────────────────────────────────────────────────────────

export const workApi = {
  /**
   * Mint a short-lived signed preview URL for a page's current version (pages only).
   * Returns a same-origin, token-authenticated URL — drop it straight into a
   * fully sandboxed <iframe src>.
   */
  async previewUrl(
    pageId: string,
  ): Promise<{ url: string; expiresIn: number; live: boolean; liveModels: string[] }> {
    const resp = await apiRequest<{
      preview_url: string;
      expires_in: number;
      live?: boolean;
      live_models?: string[];
    }>(`/api/work/${pageId}/preview-url`, { timeoutMs: LOAD_TIMEOUT_MS });
    return {
      url: `${API_BASE_URL}${resp.preview_url}`,
      expiresIn: resp.expires_in,
      // A page with live parts (`<vp-live-model>` scopes) is served with the page
      // runtime; the viewer then lets the frame run scripts (docs/work.md § Pages).
      live: !!resp.live,
      liveModels: resp.live_models ?? [],
    };
  },

  /** Every piece of work of `kind` this person reaches, across their agents, newest first. */
  async list<K extends WorkKind>(kind: K): Promise<(WorkSummary & { kind: K })[]> {
    return apiRequest<(WorkSummary & { kind: K })[]>(`/api/work?kind=${kind}`, { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /** Every piece of work of every kind this person reaches, newest first. */
  async listAll(): Promise<WorkSummary[]> {
    return apiRequest<WorkSummary[]>('/api/work', { timeoutMs: LOAD_TIMEOUT_MS });
  },

  async get(workId: string): Promise<WorkSummary> {
    return apiRequest<WorkSummary>(`/api/work/${workId}`, { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /** Every version of it. Refused (409) while it is final, and for a page
   *  while it reviews an open change to its live model. */
  async delete(workId: string): Promise<void> {
    await apiRequest<void>(`/api/work/${workId}`, { method: 'DELETE' });
  },

  /** Lock it at `version` — the one on screen, which must still be current
   *  (409 otherwise). Nothing adds a version to it until it is unlocked. */
  async markFinal(workId: string, version: number): Promise<WorkSummary> {
    return apiRequest<WorkSummary>(`/api/work/${workId}/final`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
  },

  async unlock(workId: string): Promise<WorkSummary> {
    return apiRequest<WorkSummary>(`/api/work/${workId}/final`, { method: 'DELETE' });
  },

  /** Copy its current version into a project folder (null: the root) as a new
   *  project file. A name already there is a 409 unless `onConflict` says
   *  what to do with it — the upload dialog's choices. */
  async deliver(
    workId: string,
    folderId: string | null,
    onConflict: 'ask' | 'replace' | 'keep_both' = 'ask',
  ): Promise<{ file_id: string; filename: string; folder_id: string | null; version: number }> {
    return apiRequest(`/api/work/${workId}/deliver`, {
      method: 'POST',
      body: JSON.stringify({ folder_id: folderId, on_conflict: onConflict }),
    });
  },

  /** Every send of it to someone's own file store, newest first — everyone
   *  who reaches the agent sees every one, whoever sent it. */
  async deliveries(workId: string): Promise<WorkDelivery[]> {
    return apiRequest<WorkDelivery[]>(`/api/work/${workId}/deliveries`, { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /** Send `version` to the caller's own connection, into the folder they
   *  picked in that provider's picker, as a new file. A refusal's `code` says
   *  what to do: `write_scope_missing` (re-consent), `folder_denied` (pick
   *  again), `integration_expired` (reconnect). */
  async send(
    workId: string,
    send: { version: number; integrationId: string; parentId: string },
  ): Promise<{ id: string; version: number; provider: string; name: string; url: string }> {
    return apiRequest(`/api/work/${workId}/deliveries`, {
      method: 'POST',
      body: JSON.stringify({ version: send.version, integration_id: send.integrationId, parent_id: send.parentId }),
    });
  },

  /** The current version as a file to save — a page is static: live parts show their fallbacks. */
  async download(workId: string): Promise<{ blob: Blob; filename: string }> {
    const headers = await getAuthHeaders();
    const resp = await fetch(`${API_BASE_URL}/api/work/${workId}/download`, { headers });
    if (!resp.ok) {
      const body = await resp.text().catch(() => '');
      throw new Error(`Download failed: ${resp.status} ${body || resp.statusText}`);
    }
    const hinted = /filename\*=UTF-8''([^;]+)/i.exec(resp.headers.get('Content-Disposition') ?? '')?.[1];
    let filename = 'download';
    if (hinted) {
      try {
        filename = decodeURIComponent(hinted);
      } catch {
        filename = hinted;
      }
    }
    return { blob: await resp.blob(), filename };
  },
};

/** What a piece of work is. */
export type WorkKind = 'page' | 'deck' | 'workbook';

/** A piece of an agent's work (`GET /api/work`, `GET /api/work/{id}`). */
/** One send of a version to someone's own file store (docs/work.md § Delivery). */
export interface WorkDelivery {
  id: string;
  version: number;
  provider: string;
  name: string;
  url: string;
  delivered_by: string | null;
  delivered_by_name: string | null;
  created_at: string;
}

export interface WorkSummary {
  id: string;
  kind: WorkKind;
  /** How its current version's bytes are stored. */
  format: 'html' | 'pptx' | 'xlsx';
  /** The routine run that made its current version, if one did. */
  run_id: string | null;
  /** A copy handed back by a called run: the work, version and run it came from. */
  source_work_id: string | null;
  source_version: number | null;
  source_run_id: string | null;
  /** The project of the agent it belongs to. */
  project_id: string;
  /** The agent it belongs to — whoever reaches the agent reaches its work. */
  agent_id: string;
  title: string;
  /** What its agent names it by, under `work/`. */
  name: string;
  /** The version showing; every edit is the next one. */
  version: number;
  /** Final: locked at this version until a person unlocks it; null otherwise. */
  final_version: number | null;
  final_by: string | null;
  final_at: string | null;
  conversation_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** The slugs of the live models its current version binds (a page's; empty otherwise). */
  live_models: string[];
}

/** A page: agent-authored HTML, one kind of work. */
export type PageSummary = WorkSummary & { kind: 'page' };

// ─────────────────────────────────────────────────────────────────────────────
// Live models — precomputed bundles of live models written in the epm language
// ─────────────────────────────────────────────────────────────────────────────

export interface EpmAccept {
  cells_compared: number;
  cells_matched: number;
  max_abs_diff?: number | null;
  notes?: string[];
}

/** What the Live Models index shows of one live model. */
export interface LiveModelSummary {
  slug: string;
  name: string;
  description?: string | null;
  built_at?: string | null;
  accept?: EpmAccept | null;
  outputs: string[];
  /** The version shown (0: a live model from before versions). */
  version?: number;
}

export interface EpmList {
  name: string;
  members: string[];
  groups_into?: string[];
  time?: boolean;
}

export interface EpmCell {
  at: Record<string, string>;
  v: number | string | null;
}

export interface EpmMetric {
  name: string;
  label: string;
  kind: 'input' | 'formula';
  formula?: string | null;
  reads?: string[];
  dims: string[];
  description?: string | null;
  rollup?: 'sum' | 'first' | 'last' | 'average' | 'formula' | null;
  unit?: 'currency' | 'count' | 'percent' | 'ratio' | string | null;
  source?: string | null;
  values: EpmCell[];
  rolled?: Record<string, EpmCell[]> | null;
}

export interface EpmTable {
  name: string;
  columns: string[];
  rows: (string | number | null)[][];
  row_count?: number;
}

export interface EpmBundle extends LiveModelSummary {
  version: number;
  /** For a live model with a source: the live model's own snapshot, as of when its records say, the
   *  day a copy fixes its runs at where one does, and which of the agent's views at which of its
   *  snapshots they were copied from. */
  data?: { agent_id: string; snapshot: number; agent_snapshot?: number; as_of: string | null; as_of_override?: string | null; views: string[] } | null;
  language?: string;
  built_by?: string;
  check?: { errors: number; warnings: number };
  lists: EpmList[];
  sections: { name: string; source_sheet?: string | null; metrics: string[] }[];
  metrics: EpmMetric[];
  tables?: EpmTable[];
}

/** What the browser engine runs (`package.json`): the model text and every
 *  table it binds, full. Same shape as `EpmPackage` in `client/lib/epm`. */
export interface EpmPackage {
  model_text: string;
  model_name?: string;
  tables: Record<string, { columns: string[]; rows: (string | number | null)[][] }>;
}

/** One `override` row: a value typed over base at one cell of an input. */
export interface EpmOverride {
  metric: string;
  at: Record<string, string>;
  value: number | null;
}

/** A saved scenario — shared by everyone on the agent. "Base" is no scenario. */
export interface LiveModelScenario {
  id: string;
  live_model_slug: string;
  name: string;
  description: string | null;
  overrides: EpmOverride[];
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_by: string | null;
  updated_by_name: string | null;
  updated_at: string;
  /** Optimistic-concurrency token: send it back with a replace. */
  version: number;
  /** Set when a refresh left its overrides not fitting the live model; `attention_reasons` say why. */
  needs_attention?: boolean;
  attention_reasons?: string[];
}

export interface LiveModelScenarioInput {
  name: string;
  description?: string | null;
  overrides: EpmOverride[];
}

/** Why a scenario write was refused — the bridge's `vp:error` codes. */
export type ScenarioErrorCode = 'invalid' | 'name_taken' | 'stale' | 'not_found';

/** A refused scenario write. On `stale`, `current` is the row as it is now. */
export class ScenarioApiError extends ApiError {
  constructor(
    status: number,
    message: string,
    public code: ScenarioErrorCode | undefined,
    public errors: string[] = [],
    public current?: LiveModelScenario
  ) {
    super(status, message);
    this.name = 'ScenarioApiError';
  }
}

/** A version a live model read may name: a number or `current` — or a snapshot of its source a
 *  page pins, `s<n>` (the version current on it) or `v<n>.s<m>`. */
export type EpmVersion = number | 'current' | `s${number}` | `v${number}.s${number}`;

/** One of a live model's versions. */
export interface LiveModelVersion {
  version: number;
  current: boolean;
}

const versionQuery = (version?: EpmVersion) => {
  if (version === undefined) return '';
  const pin = /^(?:v(\d+)\.)?s(\d+)$/.exec(String(version));
  if (pin) return `?snapshot=${pin[2]}${pin[1] ? `&version=${pin[1]}` : ''}`;
  return `?version=${encodeURIComponent(String(version))}`;
};

const liveModelPath = (projectId: string, slug: string) =>
  `/api/projects/${projectId}/live-models/${encodeURIComponent(slug)}`;

async function scenarioWrite<T>(endpoint: string, method: string, body?: unknown): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method,
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const detail = data.detail ?? data.error;
    const d = detail && typeof detail === 'object' ? detail : {};
    throw new ScenarioApiError(
      response.status,
      errorMessage(detail) || `Request failed: ${response.statusText}`,
      d.code,
      d.errors ?? [],
      d.current
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const liveModelsApi = {
  /** The live models under one project, shared by its agents. */
  list(projectId: string): Promise<LiveModelSummary[]> {
    return apiRequest<LiveModelSummary[]>(`/api/projects/${projectId}/live-models`, { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /** Delete a live model — every version and its scenarios (the project owner's). The pages
   *  bound to it stay, showing what they showed when built. */
  async remove(projectId: string, slug: string): Promise<void> {
    await apiRequest<void>(liveModelPath(projectId, slug), { method: 'DELETE' });
  },

  /** The bundle of the current version, or of the one `version` names. */
  get(projectId: string, slug: string, version?: EpmVersion): Promise<EpmBundle> {
    return apiRequest<EpmBundle>(`${liveModelPath(projectId, slug)}${versionQuery(version)}`, { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /** The live model's text, in the epm language. */
  async text(projectId: string, slug: string, version?: EpmVersion): Promise<string> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}${liveModelPath(projectId, slug)}/text${versionQuery(version)}`,
      { headers }
    );
    if (!response.ok) throw new ApiError(response.status, `Request failed: ${response.statusText}`);
    return response.text();
  },

  /** What the browser engine runs; 404 until the live model's export has written it. */
  package(projectId: string, slug: string, version?: EpmVersion): Promise<EpmPackage> {
    return apiRequest<EpmPackage>(`${liveModelPath(projectId, slug)}/package${versionQuery(version)}`, { timeoutMs: LOAD_TIMEOUT_MS });
  },

  /** The live model's versions, newest first, and which one is current. */
  versions(projectId: string, slug: string): Promise<{ current: number; versions: LiveModelVersion[] }> {
    return apiRequest(`${liveModelPath(projectId, slug)}/versions`);
  },

  /** The live model's saved scenarios, shared by everyone on the agent. Writes throw
   *  `ScenarioApiError` (`stale` carries the current row). */
  scenarios: {
    list(projectId: string, slug: string): Promise<LiveModelScenario[]> {
      return apiRequest<LiveModelScenario[]>(`${liveModelPath(projectId, slug)}/scenarios`, { timeoutMs: LOAD_TIMEOUT_MS });
    },

    create(projectId: string, slug: string, input: LiveModelScenarioInput): Promise<LiveModelScenario> {
      return scenarioWrite<LiveModelScenario>(`${liveModelPath(projectId, slug)}/scenarios`, 'POST', input);
    },

    /** Replace name, description and overrides; `version` is the one last read. */
    replace(
      projectId: string,
      slug: string,
      id: string,
      input: LiveModelScenarioInput & { version: number }
    ): Promise<LiveModelScenario> {
      return scenarioWrite<LiveModelScenario>(
        `${liveModelPath(projectId, slug)}/scenarios/${encodeURIComponent(id)}`, 'PUT', input
      );
    },

    remove(projectId: string, slug: string, id: string): Promise<void> {
      return scenarioWrite<void>(
        `${liveModelPath(projectId, slug)}/scenarios/${encodeURIComponent(id)}`, 'DELETE'
      );
    },
  },
};
